export const javaApiExample = `import java.io.IOException;
import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.nio.charset.StandardCharsets;
import java.time.Duration;
import java.time.Instant;
import java.util.HashMap;
import java.util.Map;
import java.util.UUID;
import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.util.Base64;
import java.security.InvalidKeyException;
import java.security.NoSuchAlgorithmException;
import com.fasterxml.jackson.databind.ObjectMapper;
import com.fasterxml.jackson.annotation.JsonProperty;

/**
 * ReasonShield API Client demonstrating dual authentication
 * with API key and webhook signature verification.
 */
public class ReasonShieldClient {
    // API configuration constants
    private static final String API_BASE_URL = "https://app.reasonshield.com/api/v1";
    private static final String API_ENDPOINT_PATH = "/lis-webhook/vendor";
    private static final String ALGORITHM = "HmacSHA256";
    
    private final String vendorId;
    private final String apiKey;
    private final String webhookSecret;
    private final HttpClient httpClient;
    private final ObjectMapper objectMapper;
    
    /**
     * Constructor for the ReasonShield client.
     * 
     * @param vendorId Your assigned vendor ID
     * @param apiKey Your API key for authentication
     * @param webhookSecret Your webhook secret for signature generation
     */
    public ReasonShieldClient(String vendorId, String apiKey, String webhookSecret) {
        if (vendorId == null || vendorId.isBlank() || 
            apiKey == null || apiKey.isBlank() || 
            webhookSecret == null || webhookSecret.isBlank()) {
            throw new IllegalArgumentException("All authentication parameters are required");
        }
        
        this.vendorId = vendorId;
        this.apiKey = apiKey;
        this.webhookSecret = webhookSecret;
        
        this.httpClient = HttpClient.newBuilder()
                .version(HttpClient.Version.HTTP_2)
                .connectTimeout(Duration.ofSeconds(30))
                .build();
                
        this.objectMapper = new ObjectMapper();
    }
    
    /**
     * Generates an HMAC-SHA256 signature for the provided payload.
     * 
     * @param payload The JSON string to sign
     * @return The hex-encoded signature
     * @throws SignatureGenerationException if signing fails
     */
    private String generateSignature(String payload) throws SignatureGenerationException {
        try {
            SecretKeySpec secretKeySpec = new SecretKeySpec(
                webhookSecret.getBytes(StandardCharsets.UTF_8), ALGORITHM);
            
            Mac mac = Mac.getInstance(ALGORITHM);
            mac.init(secretKeySpec);
            
            byte[] hmacBytes = mac.doFinal(payload.getBytes(StandardCharsets.UTF_8));
            
            // Convert to hex string
            StringBuilder sb = new StringBuilder();
            for (byte b : hmacBytes) {
                sb.append(String.format("%02x", b));
            }
            
            return sb.toString();
        } catch (NoSuchAlgorithmException | InvalidKeyException e) {
            throw new SignatureGenerationException("Failed to generate signature", e);
        }
    }
    
    /**
     * Submits test data to the ReasonShield API.
     * 
     * @param testData The test data map to submit
     * @return SubmitResponse object containing data_id and status
     * @throws ApiException if the API request fails
     */
    public SubmitResponse submitData(Map<String, Object> testData) throws ApiException {
        try {
            // Prepare request payload
            Map<String, Object> payload = new HashMap<>();
            payload.put("vendor_id", vendorId);
            payload.put("timestamp", Instant.now().toString());
            payload.put("request_id", UUID.randomUUID().toString());
            payload.put("test_data", testData);
            
            // Convert payload to JSON string
            String jsonPayload = objectMapper.writeValueAsString(payload);
            
            // Generate signature
            String signature = generateSignature(jsonPayload);
            
            // Create HTTP request with both authentication methods
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(API_BASE_URL + API_ENDPOINT_PATH + "/submit-data"))
                .header("Content-Type", "application/json")
                .header("Authorization", "Bearer " + apiKey)        // API Key Authentication
                .header("X-Vendor-Signature", signature)            // Signature Verification
                .POST(HttpRequest.BodyPublishers.ofString(jsonPayload))
                .timeout(Duration.ofSeconds(30))
                .build();
                
            // Send request
            HttpResponse<String> response = httpClient.send(
                request, HttpResponse.BodyHandlers.ofString());
                
            // Check response status
            if (response.statusCode() != 202) {
                throw new ApiException("Failed to submit data: HTTP " + 
                    response.statusCode() + " " + response.body());
            }
            
            // Parse and return response
            return objectMapper.readValue(response.body(), SubmitResponse.class);
            
        } catch (IOException | InterruptedException | SignatureGenerationException e) {
            throw new ApiException("API request failed", e);
        }
    }
    
    /**
     * Checks the status of previously submitted data.
     * 
     * @param dataId The unique identifier for the data
     * @return StatusResponse containing processing status and report availability
     * @throws ApiException if the API request fails
     */
    public StatusResponse checkStatus(String dataId) throws ApiException {
        try {
            // Create HTTP request with API key authentication only
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(API_BASE_URL + API_ENDPOINT_PATH + "/status/" + dataId))
                .header("Authorization", "Bearer " + apiKey)
                .GET()
                .timeout(Duration.ofSeconds(30))
                .build();
                
            // Send request
            HttpResponse<String> response = httpClient.send(
                request, HttpResponse.BodyHandlers.ofString());
                
            // Check response status
            if (response.statusCode() != 200) {
                throw new ApiException("Failed to check status: HTTP " + 
                    response.statusCode() + " " + response.body());
            }
            
            // Parse and return response
            return objectMapper.readValue(response.body(), StatusResponse.class);
            
        } catch (IOException | InterruptedException e) {
            throw new ApiException("API request failed", e);
        }
    }
    
    /**
     * Retrieves the report for processed data.
     * 
     * @param dataId The unique identifier for the data
     * @return ReportResponse containing report data
     * @throws ApiException if the API request fails
     */
    public ReportResponse getReport(String dataId) throws ApiException {
        try {
            // Create HTTP request with API key authentication only
            HttpRequest request = HttpRequest.newBuilder()
                .uri(URI.create(API_BASE_URL + API_ENDPOINT_PATH + "/report/" + dataId))
                .header("Authorization", "Bearer " + apiKey)
                .GET()
                .timeout(Duration.ofSeconds(30))
                .build();
                
            // Send request
            HttpResponse<String> response = httpClient.send(
                request, HttpResponse.BodyHandlers.ofString());
                
            // Check response status
            if (response.statusCode() != 200) {
                throw new ApiException("Failed to retrieve report: HTTP " + 
                    response.statusCode() + " " + response.body());
            }
            
            // Parse and return response
            return objectMapper.readValue(response.body(), ReportResponse.class);
            
        } catch (IOException | InterruptedException e) {
            throw new ApiException("API request failed", e);
        }
    }
    
    // Response data classes
    
    public static class SubmitResponse {
        @JsonProperty("data_id")
        private String dataId;
        
        @JsonProperty("status")
        private String status;
        
        public String getDataId() { return dataId; }
        public String getStatus() { return status; }
    }
    
    public static class StatusResponse {
        @JsonProperty("status")
        private String status;
        
        @JsonProperty("report_available")
        private boolean reportAvailable;
        
        public String getStatus() { return status; }
        public boolean isReportAvailable() { return reportAvailable; }
    }
    
    public static class ReportResponse {
        @JsonProperty("report_data")
        private ReportData reportData;
        
        public ReportData getReportData() { return reportData; }
    }
    
    public static class ReportData {
        @JsonProperty("risk_assessment")
        private RiskAssessment riskAssessment;
        
        public RiskAssessment getRiskAssessment() { return riskAssessment; }
    }
    
    public static class RiskAssessment {
        @JsonProperty("overall_risk")
        private String overallRisk;
        
        public String getOverallRisk() { return overallRisk; }
    }
    
    // Custom exceptions
    
    public static class ApiException extends Exception {
        public ApiException(String message) {
            super(message);
        }
        
        public ApiException(String message, Throwable cause) {
            super(message, cause);
        }
    }
    
    public static class SignatureGenerationException extends Exception {
        public SignatureGenerationException(String message, Throwable cause) {
            super(message, cause);
        }
    }
    
    /**
     * Main method demonstrating usage of the ReasonShield client.
     */
    public static void main(String[] args) {
        try {
            // Load authentication from environment (or provide default test values)
            String vendorId = System.getenv().getOrDefault("VENDOR_ID", "your-vendor-id");
            String apiKey = System.getenv().getOrDefault("VENDOR_API_KEY", "your-api-key");
            String webhookSecret = System.getenv().getOrDefault("VENDOR_WEBHOOK_SECRET", "your-webhook-secret");
            
            // Initialize client
            ReasonShieldClient client = new ReasonShieldClient(vendorId, apiKey, webhookSecret);
            
            // Prepare test data
            Map<String, Object> patientInfo = new HashMap<>();
            patientInfo.put("patient_id", "PT-789456");
            patientInfo.put("age", 45);
            patientInfo.put("gender", "M");
            
            Map<String, Object> collectionInfo = new HashMap<>();
            collectionInfo.put("collection_date", "2024-09-10T13:11:00-06:00");
            collectionInfo.put("specimen_type", "nasopharyngeal swab");
            
            Map<String, Object> virus1 = new HashMap<>();
            virus1.put("name", "Influenza A");
            virus1.put("result", "Not Detected");
            
            Map<String, Object> virus2 = new HashMap<>();
            virus2.put("name", "SARS-CoV-2");
            virus2.put("result", "Detected");
            
            Map<String, Object> bacteria1 = new HashMap<>();
            bacteria1.put("name", "Streptococcus pneumoniae");
            bacteria1.put("result", "Not Detected");
            
            Map<String, Object> bacteria2 = new HashMap<>();
            bacteria2.put("name", "Mycoplasma pneumoniae");
            bacteria2.put("result", "Not Detected");
            
            Map<String, Object> testDetails = new HashMap<>();
            testDetails.put("viruses", new Object[]{virus1, virus2});
            testDetails.put("bacteria", new Object[]{bacteria1, bacteria2});
            
            Map<String, Object> testData = new HashMap<>();
            testData.put("test_id", "12345");
            testData.put("panel_type", "RESPIRATORY");
            testData.put("patient_info", patientInfo);
            testData.put("collection_info", collectionInfo);
            testData.put("test_details", testDetails);
            
            // Submit data
            System.out.println("Submitting test data...");
            SubmitResponse submitResponse = client.submitData(testData);
            System.out.println("Data submitted successfully. ID: " + submitResponse.getDataId());
            
            // Check status
            System.out.println("Checking status...");
            StatusResponse statusResponse = client.checkStatus(submitResponse.getDataId());
            System.out.println("Status: " + statusResponse.getStatus());
            
            // In a real implementation, you would poll until processing is complete
            // For demonstration, we'll simulate a basic polling approach
            int maxAttempts = 5;
            int attempt = 0;
            
            while (!statusResponse.getStatus().equals("COMPLETED") && attempt < maxAttempts) {
                System.out.println("Waiting for processing to complete (attempt " + 
                    (attempt + 1) + "/" + maxAttempts + ")...");
                
                // Wait 5 seconds between checks
                Thread.sleep(5000);
                
                statusResponse = client.checkStatus(submitResponse.getDataId());
                System.out.println("Updated status: " + statusResponse.getStatus());
                attempt++;
            }
            
            // If processing is complete and report is available, get the report
            if (statusResponse.getStatus().equals("COMPLETED") && statusResponse.isReportAvailable()) {
                System.out.println("Retrieving report...");
                ReportResponse reportResponse = client.getReport(submitResponse.getDataId());
                String overallRisk = reportResponse.getReportData().getRiskAssessment().getOverallRisk();
                System.out.println("Retrieved report with risk assessment: " + overallRisk);
            } else {
                System.out.println("Report not yet available.");
            }
            
        } catch (Exception e) {
            System.err.println("Error: " + e.getMessage());
            e.printStackTrace();
        }
    }
}
`; 